// @flow

import React, { useEffect } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { View, ColumnsContainer, Text } from "@components";
import actionCreators from "@modules/service/actionCreator";
import clientActionCreators from "@modules/client/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { getServiceSettings } from "@modules/client/selectors";
import type { State } from "@store/reducers";
import type { RegistrationEmails } from "@modules/service/reducer/system-access/types";
import type { AbsenceManagementSettings } from "@modules/client/reducer/service/types";

type PropsFromState = $Exact<{
  bulkRegistrationEmailTargets: number,
  registrationEmails: ?RegistrationEmails,
  employeeRegistrationEmailTargets: number,
  serviceSettings: AbsenceManagementSettings,
}>;

type PropsFromDispatch = $Exact<{
  openRegistrationEmailsModal: Function,
  openEmployeeRegistrationEmailsModal: Function,
  getBulkRegistrationEmailsTargets: typeof actionCreators.GetBulkRegistrationEmailsTargets,
  getEmployeeRegistrationEmailsTargets: typeof actionCreators.GetEmployeeRegistrationEmailsTargets,
  getPlatformDataSecurity: typeof actionCreators.GetPlatformDataSecurity,
  showStandardRegistrationEmailsModal: Function,
  // openRegistrationEmailsExportModal: typeof actionCreators.ToggleRegistrationEmailsExportModal,
  exportBulkRegistrationEmails: typeof actionCreators.ExportBulkRegistrationEmails,
  getClientService: typeof clientActionCreators.GetServiceAbsenceManagementSettings,
}>;

type OwnProps = $Exact<{
  clientId: ?number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function SystemAccessEmails(props: Props) {
  const { clientId } = props;

  function rowsData() {
    let columns = [
      {
        columnTitle: props.t("STANDARD_REGISTRATION_EMAILS"),
        columnButtons: [
          {
            materialIcon: "edit",
            text: props.t("EDIT"),
            onClick: props.showStandardRegistrationEmailsModal,
          },
        ],
        data: [
          [
            <div
              style={{ marginTop: "0.5rem" }}
              key={`${props.t("DEFAULT_INTRODUCTION_TEXT")}-title`}
            >
              {props.t("DEFAULT_INTRODUCTION_TEXT")}
            </div>,
            <div
              style={{ lineHeight: "21px", marginTop: "0.5rem" }}
              key={`${props.t("DEFAULT_INTRODUCTION_TEXT")}-desc`}
            >
              {props.registrationEmails?.introductionTextDefault}
            </div>,
          ],
        ],
      },
      {
        columnTitle: props.t("MANAGERS_HR_REGISTRATIONS"),
        columnButtons: [
          {
            materialIcon: "email",
            text: props.t("SEND"),
            onClick: props.openRegistrationEmailsModal,
          },
        ],
        data: [[registrationsData("bulk")]],
      },
    ];

    if (props.serviceSettings?.mobileAppAccess === true) {
      columns.push({
        columnTitle: props.t("EMPLOYEE_REGISTRATIONS"),
        columnButtons: [
          {
            materialIcon: "email",
            text: props.t("SEND"),
            onClick: props.openEmployeeRegistrationEmailsModal,
          },
        ],
        data: [[registrationsData("employee")]],
      });
    }

    return [
      {
        columns,
      },
    ];
  }

  useEffect(() => {
    props.getBulkRegistrationEmailsTargets({ clientId });
    props.getEmployeeRegistrationEmailsTargets({ clientId });
    props.getPlatformDataSecurity({ clientId });
    props.getClientService({ clientId });
  }, [clientId]);

  function registrationsData(type: string) {
    function exportBulkMails() {
      props.exportBulkRegistrationEmails({
        clientId: props.clientId,
      });
    }

    const data = {
      bulk: {
        html: props.t("BULK_REGISTRATIONS_TEXT", {
          count: (
            <strong
              className="text-pumpkin-orange text-decoration-underline pointer"
              onClick={Boolean(props.bulkRegistrationEmailTargets) && exportBulkMails}
            >
              {props.bulkRegistrationEmailTargets}
            </strong>
          ),
        }),
      },
      employee: {
        html: props.t("EMPLOYEE_REGISTRATIONS_TEXT", {
          count: <Text type="strong">{props.employeeRegistrationEmailTargets}</Text>,
        }),
      },
    };

    return data[type].html;
  }

  return (
    <View>
      <ColumnsContainer className="w-100" rows={rowsData()} />
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    bulkRegistrationEmailTargets: serviceSelectors.getBulkRegistrationEmailTargets(state),
    registrationEmails: serviceSelectors.getRegistrationEmails(state),
    employeeRegistrationEmailTargets: serviceSelectors.getEmployeeRegistrationEmailTargets(state),
    serviceSettings: getServiceSettings(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  openRegistrationEmailsModal: () => actionCreators.ToggleRegistrationEmailsModal(true),
  openEmployeeRegistrationEmailsModal: () =>
    actionCreators.ToggleEmployeeRegistrationEmailsModal(true),
  getBulkRegistrationEmailsTargets: actionCreators.GetBulkRegistrationEmailsTargets,
  getEmployeeRegistrationEmailsTargets: actionCreators.GetEmployeeRegistrationEmailsTargets,
  getPlatformDataSecurity: actionCreators.GetPlatformDataSecurity,
  showStandardRegistrationEmailsModal: () =>
    actionCreators.ToggleStandardRegistrationEmailsModal(true),
  // openRegistrationEmailsExportModal: () => actionCreators.ToggleRegistrationEmailsExportModal(true),
  exportBulkRegistrationEmails: actionCreators.ExportBulkRegistrationEmails,
  getClientService: clientActionCreators.GetServiceAbsenceManagementSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(SystemAccessEmails));
